import { Button, Flex } from '@chakra-ui/react'
import {
  ContentNodeUtils,
  DataTypeConfigUtils,
  InputValueContentNode,
  InputValueDataTypeConfig,
} from '@helo/cms'
import { Assert, Value, flow, pipe } from '@helo/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { useMemo } from 'react'

import { useIsDark } from '../../../hooks/useIsDark'
import { FilterChip } from '../FilterChip/FilterChip'
import {
  CONDITION_LABELS,
  DATE_FILTER_CONDITION_OPTIONS,
  DATE_FILTER_CONDITION_OPTIONS_EXTENDED,
  LoadOptionsForPickListFn,
  hasFilterValue,
} from '../FilterEditor/FilterEditor.parts'
import {
  DATE_TIME_FILTER_INPUT,
  isAnalyticsDateFilterTypeName,
} from '../dateFilterInputAdapters'
import { FilterDisplayFormatting } from '../filterDisplayFormatting'

/** Format date for chip display via default dateFormat (medium) */
const formatDateForDisplay = (dateStr: string): string => {
  return FilterDisplayFormatting.date(dateStr)
}

/** Format date as MM-DD-YYYY for range display on chip */
const formatDateForRangeDisplay = (dateStr: string): string => {
  return FilterDisplayFormatting.dateRange(dateStr)
}

const chipsScaleFade = {
  initial: { opacity: 0, scale: 0.95 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.15,
      ease: [0, 0, 0.2, 1] as [number, number, number, number],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.1,
      ease: [0.4, 0, 1, 1] as [number, number, number, number],
    },
  },
}

export type FilterChipData = {
  id?: string
  condition?: string
  label: string
  value?: string | string[]
}

export type FilterRow = {
  key: string
  contentNode: InputValueContentNode
}

// Helper to get condition options from config (similar to FilterEditorRow)
const getConditionOptions = (
  config: InputValueDataTypeConfig,
): Array<{ label: string; value: string }> => {
  if (!config?.ofType) return []

  const obj = pipe(
    config,
    DataTypeConfigUtils.getOfType(),
    Assert.orReturn(DataTypeConfigUtils.isKind('INPUT_OBJECT'), () => null),
  )

  if (!obj) return []

  try {
    const fields = DataTypeConfigUtils.getFields()(obj)
    return fields
      .filter(field => field.key !== 'not' && field.key !== 'mode')
      .map(field => ({
        label:
          CONDITION_LABELS[field.key] ??
          (field.state.label as string) ??
          field.key,
        value: field.key,
      }))
  } catch {
    return []
  }
}

// Helper to extract condition and value from filter value object
export const extractFilterData = (
  contentNode: InputValueContentNode,
): { condition?: string; value?: string | string[] } => {
  const filterValue = contentNode.data as Record<string, any> | undefined
  if (!filterValue || typeof filterValue !== 'object') {
    return {}
  }

  const config =
    typeof contentNode.getDataTypeConfig === 'function'
      ? contentNode.getDataTypeConfig()
      : (contentNode as unknown as InputValueDataTypeConfig)

  // Get condition options to use their labels
  const conditionOptions = getConditionOptions(config)

  const filterTypeName =
    (contentNode.ofType as { typeName?: string } | undefined)?.typeName ??
    (config.ofType as { typeName?: string } | undefined)?.typeName
  const isDateTimeFilter = filterTypeName === DATE_TIME_FILTER_INPUT
  const isCubeDateTimeFilter = isAnalyticsDateFilterTypeName(filterTypeName)
  const isNumberFilter =
    filterTypeName === 'IntFilterInput' || filterTypeName === 'FloatFilterInput'

  // Date filter: blank/notBlank are stored as equals: null and not: { equals: null }
  if (isDateTimeFilter) {
    if (filterValue.not?.equals === null) {
      const label =
        DATE_FILTER_CONDITION_OPTIONS.find(o => o.value === 'notBlank')
          ?.label ?? 'Is Present'
      return { condition: label, value: undefined }
    }
    if (filterValue.equals === null) {
      const label =
        DATE_FILTER_CONDITION_OPTIONS.find(o => o.value === 'blank')?.label ??
        'Is Blank'
      return { condition: label, value: undefined }
    }
  }

  // String/number filter: Is Blank is stored as equals: null (otherwise we'd show "Equals")
  if (filterValue.equals === null) {
    const blankOption = conditionOptions.find(opt => opt.value === 'blank')
    return {
      condition: blankOption?.label ?? CONDITION_LABELS.blank ?? 'Is Blank',
      value: undefined,
    }
  }

  // CubeStringFilter / CubeDateTimeFilter: blank/present use `set`
  if (filterValue.set === false) {
    const blankOption = conditionOptions.find(opt => opt.value === 'blank')
    return {
      condition: blankOption?.label ?? CONDITION_LABELS.blank ?? 'Is Blank',
      value: undefined,
    }
  }
  if (filterValue.set === true) {
    const notBlankOption = conditionOptions.find(
      opt => opt.value === 'notBlank',
    )
    return {
      condition: notBlankOption?.label ?? 'Is Present',
      value: undefined,
    }
  }

  // CubeStringFilter flat negation ops
  if (filterValue.notEquals !== undefined) {
    const rawValue = Array.isArray(filterValue.notEquals)
      ? String(filterValue.notEquals[0] ?? '')
      : String(filterValue.notEquals)
    return {
      condition: isCubeDateTimeFilter
        ? 'Excludes'
        : (CONDITION_LABELS.notEqual ?? 'Does Not Equal'),
      value: isCubeDateTimeFilter ? formatDateForDisplay(rawValue) : rawValue,
    }
  }
  if (filterValue.notContains !== undefined) {
    const raw = filterValue.notContains
    return {
      condition: CONDITION_LABELS.notContains ?? 'Does Not Contain',
      value: Array.isArray(raw) ? raw.join(', ') : String(raw),
    }
  }

  // Check for 'not' conditions first
  if (filterValue.not && typeof filterValue.not === 'object') {
    const notValue = filterValue.not as Record<string, any>
    if (notValue.equals === null) {
      const notBlankOption = conditionOptions.find(
        opt => opt.value === 'notBlank',
      )
      return {
        condition: notBlankOption?.label ?? 'Is Present',
        value: undefined,
      }
    }
    if (notValue.equals !== undefined) {
      const rawValue = String(notValue.equals)
      return {
        condition: isDateTimeFilter
          ? 'Excludes'
          : (CONDITION_LABELS.notEqual ?? 'Does Not Equal'),
        value: isDateTimeFilter
          ? formatDateForDisplay(rawValue)
          : isNumberFilter
            ? FilterDisplayFormatting.number(notValue.equals)
            : rawValue,
      }
    }
    if (notValue.contains !== undefined) {
      return {
        condition: CONDITION_LABELS.notContains ?? 'Does Not Contain',
        value: String(notValue.contains),
      }
    }
  }

  // Cube date range: inDateRange [from, to]
  if (isCubeDateTimeFilter) {
    const inDateRange = filterValue.inDateRange as unknown
    if (
      Array.isArray(inDateRange) &&
      inDateRange[0] != null &&
      inDateRange[1] != null
    ) {
      return {
        condition:
          DATE_FILTER_CONDITION_OPTIONS.find(o => o.value === 'inRange')
            ?.label ?? 'Range',
        value: [
          formatDateForRangeDisplay(String(inDateRange[0])),
          formatDateForRangeDisplay(String(inDateRange[1])),
        ],
      }
    }
  }

  // Number/float range: gte + lte means "In Range"
  if (filterValue.gte != null && filterValue.lte != null && !isDateTimeFilter) {
    return {
      condition: CONDITION_LABELS.inRange ?? 'In Range',
      value: isNumberFilter
        ? [
            FilterDisplayFormatting.number(filterValue.gte),
            FilterDisplayFormatting.number(filterValue.lte),
          ]
        : [String(filterValue.gte), String(filterValue.lte)],
    }
  }

  // Check for direct conditions (excluding 'not' and 'mode')
  const conditionKeys = Object.keys(filterValue).filter(
    key => key !== 'not' && key !== 'mode',
  )

  if (conditionKeys.length === 0) {
    return {}
  }

  // For date filters, prefer lt/gt over equals so "Is Before"/"Is After" show correctly
  // when URL/merge leaves both keys (e.g. equals from initial state); otherwise first key wins and can show "Date"
  let conditionKey = conditionKeys[0]
  if (isDateTimeFilter) {
    if (filterValue.lt != null) conditionKey = 'lt'
    else if (filterValue.gt != null) conditionKey = 'gt'
  }

  const value = filterValue[conditionKey]

  // Find the condition option to get the label
  const conditionOption = conditionOptions.find(
    opt => opt.value === conditionKey,
  )
  let condition =
    conditionOption?.label ?? CONDITION_LABELS[conditionKey] ?? conditionKey
  // For date filters, use extended option labels for lt/gt (Is Before / Is After)
  if (isDateTimeFilter && conditionKey === 'lt') {
    condition =
      DATE_FILTER_CONDITION_OPTIONS_EXTENDED.find(o => o.value === 'lessThan')
        ?.label ?? 'Is Before'
  }
  if (isDateTimeFilter && conditionKey === 'gt') {
    condition =
      DATE_FILTER_CONDITION_OPTIONS_EXTENDED.find(
        o => o.value === 'greaterThan',
      )?.label ?? 'Is After'
  }

  // Handle array values (for 'in' condition)
  if (Array.isArray(value)) {
    return {
      condition,
      value: isNumberFilter
        ? value.map(FilterDisplayFormatting.number)
        : (value.filter(Boolean) as string[]),
    }
  }

  // Handle null values (for blank conditions)
  if (value === null) {
    // Date filter blank already handled above; for others use resolved condition
    return { condition, value: undefined }
  }

  // Format date for display (e.g. "Dec 16, 2025") when field is a date filter
  if ((isDateTimeFilter || isCubeDateTimeFilter) && typeof value === 'string') {
    const gte = filterValue.gte as string | undefined
    const lte = filterValue.lte as string | undefined
    if (gte != null && lte != null) {
      return {
        // Match DATE_FILTER_CONDITION_OPTIONS label ("Range"), not number "In Range"
        condition:
          DATE_FILTER_CONDITION_OPTIONS.find(o => o.value === 'inRange')
            ?.label ?? 'Range',
        value: [formatDateForRangeDisplay(gte), formatDateForRangeDisplay(lte)],
      }
    }
    return { condition, value: formatDateForDisplay(value) }
  }

  return {
    condition,
    value: isNumberFilter
      ? FilterDisplayFormatting.number(value)
      : String(value),
  }
}

export interface FilterChipsEditorProps {
  filters?: InputValueDataTypeConfig[]
  filterInputValue?: InputValueContentNode
  onChange?: (inputValueContentNode: InputValueContentNode) => void
  /** Async load options for "Is Any Of" pick list when editing a chip. */
  loadOptionsForPickList?: LoadOptionsForPickListFn
  filterRows?: Array<FilterRow>
  chips?: FilterChipData[] // Keep for backwards compatibility
  onRemove?: (chip: FilterChipData, index: number) => void
  onUpdate?: (chip: FilterChipData, index: number) => void
  onClear?: () => void
  /** When set, the chip at this index has its edit popover open on mount (e.g. for Storybook). */
  defaultOpenChipIndex?: number
  onClick?: () => void
}

export const FilterChipsEditor = ({
  filters,
  filterInputValue,
  onChange,
  loadOptionsForPickList,
  filterRows,
  chips,
  onRemove,
  onUpdate,
  onClear,
  onClick,
  defaultOpenChipIndex,
  ...props
}: FilterChipsEditorProps) => {
  const isDark = useIsDark()

  // Build chips from filters filtered to those with data
  const chipsFromConfigs = useMemo(() => {
    if (!filters || !filterInputValue) {
      return []
    }

    // Read from filterInputValue.data directly (same as FilterEditor does)
    // filterInputValue.data is an object where keys match field keys
    // The structure: filterInputValue.data = { fieldKey1: { contains: "value" }, fieldKey2: { equals: "value" } }
    const filterData = filterInputValue.data

    if (
      !filterData ||
      typeof filterData !== 'object' ||
      Array.isArray(filterData)
    ) {
      return []
    }

    const filterDataObj = filterData as Record<string, unknown>
    const chipsData: FilterChipData[] = []

    for (const [fieldKey, fieldValue] of Object.entries(filterDataObj)) {
      // Check if this field has a value
      if (hasFilterValue(fieldValue)) {
        // Get the config from filters
        const matchingConfig = filters.find(config => config.key === fieldKey)

        if (matchingConfig) {
          const fieldContentNode =
            ContentNodeUtils.createContentNodeWithSelection(
              matchingConfig,
              fieldValue,
            ) as InputValueContentNode

          const { condition, value } = extractFilterData(fieldContentNode)
          chipsData.push({
            id: fieldKey,
            condition,
            label: (matchingConfig.state.label as string) || fieldKey,
            value,
          })
        }
      }
    }

    return chipsData
  }, [filters, filterInputValue])

  // Convert filterRows to chips if provided
  const chipsFromRows: FilterChipData[] = useMemo(() => {
    return (
      filterRows?.map(row => {
        const { condition, value } = extractFilterData(row.contentNode)
        return {
          id: row.key,
          condition,
          label:
            (row.contentNode.getDataTypeConfig()?.state?.label as string) ||
            row.key,
          value,
        }
      }) || []
    )
  }, [filterRows])

  // Use chipsFromConfigs first, then chipsFromRows, then chips prop
  const chipsData: FilterChipData[] =
    chipsFromConfigs.length > 0
      ? chipsFromConfigs
      : chipsFromRows.length > 0
        ? chipsFromRows
        : chips || []

  const handleRemove = (chip: FilterChipData, index: number) => {
    // If custom onRemove handler provided, use it
    if (onRemove) {
      onRemove(chip, index)
      return
    }

    // Otherwise, directly update filterInputValue by removing the field
    if (!filterInputValue || !onChange || !chip.id) {
      return
    }

    // Get the filter object content node
    const filterObjectContentNode = pipe(
      filterInputValue,
      Assert.orReturn(Value.isNotNullable, () => null),
      ContentNodeUtils.getOfType(),
      Assert.orReturn(ContentNodeUtils.isKind('INPUT_OBJECT'), () => null),
    )

    if (!filterObjectContentNode) {
      return
    }

    // Get current filter data and remove the field
    const existingData =
      (filterInputValue.data as Record<string, unknown>) || {}
    const filterData: Record<string, unknown> = { ...existingData }
    delete filterData[chip.id]

    // Update the filter object content node
    const updatedFilterObject = ContentNodeUtils.updateContentNode(filterData)(
      filterObjectContentNode,
    )

    // Update the parent filterInputValue using the same pattern as FilterEditor
    const updatedInputValueContentNode = pipe(
      filterInputValue,
      ContentNodeUtils.updateChildContentNode(
        flow(
          Assert.orThrow(
            ContentNodeUtils.isKind('INPUT_OBJECT'),
            () => new Error('Expected INPUT_OBJECT'),
          ),
          () => updatedFilterObject,
        ),
      ),
    )

    onChange(updatedInputValueContentNode)
  }

  // Fragment so each chip (and Clear) is a direct flex child of FilterEditor's row –
  // they wrap together with the filter button instead of the whole chip row moving down.
  return (
    <>
      <AnimatePresence>
        {chipsData.map((chip, index) => {
          const fieldConfig =
            chip.id &&
            (filters?.find(c => c.key === chip.id) ??
              filterRows
                ?.find(r => r.key === chip.id)
                ?.contentNode.getDataTypeConfig())
          const fieldValue =
            chip.id && filterInputValue
              ? (filterInputValue.data as Record<string, unknown>)?.[chip.id]
              : undefined
          const fieldContentNode: InputValueContentNode | undefined =
            filterInputValue && fieldConfig && chip.id
              ? (ContentNodeUtils.createContentNodeWithSelection(
                  fieldConfig,
                  fieldValue !== undefined && fieldValue !== null
                    ? (fieldValue as Record<string, unknown>)
                    : {},
                ) as InputValueContentNode)
              : undefined
          return (
            <motion.div
              key={chip.id || index}
              variants={chipsScaleFade}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ display: 'inline-flex' }}
            >
              <FilterChip
                condition={chip.condition}
                label={chip.label}
                value={chip.value}
                onRemove={() => handleRemove(chip, index)}
                filterInputValue={
                  filterInputValue && onChange ? filterInputValue : undefined
                }
                fieldContentNode={fieldContentNode}
                availableFilters={fieldConfig ? [fieldConfig] : undefined}
                loadOptionsForPickList={loadOptionsForPickList}
                onChange={onChange}
                defaultIsPopoverOpen={defaultOpenChipIndex === index}
              />
            </motion.div>
          )
        })}
      </AnimatePresence>
      {onClear && chipsData.length > 0 && (
        <Button
          data-testid="filter-chips-clear-button"
          variant="ghost"
          borderRadius="4px"
          padding="8px"
          onClick={onClear}
          fontSize="12px"
          color={isDark ? 'presets.neutral.0' : 'presets.turquoise.90'}
        >
          Clear
        </Button>
      )}
    </>
  )
}
